import { Capacitor } from '@capacitor/core'
import type { PluginListenerHandle } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { supabase } from './supabase'

// Default to 'production'; the Edge Function will detect sandbox on a 400 BadDeviceToken
// and update the row accordingly. Override locally via VITE_APNS_ENVIRONMENT=sandbox.
const ENVIRONMENT: 'sandbox' | 'production' =
  (import.meta.env.VITE_APNS_ENVIRONMENT as 'sandbox' | 'production') ?? 'production'

let registrationListener: PluginListenerHandle | null = null
let registrationErrorListener: PluginListenerHandle | null = null

async function saveToken(userId: string, token: string) {
  // Check if this token is already registered. If so, preserve the existing
  // environment value (the Edge Function may have corrected it after a 400
  // BadDeviceToken retry). Only set environment on the initial insert.
  const { data: existing } = await supabase
    .from('push_tokens')
    .select('id')
    .eq('token', token)
    .maybeSingle()

  if (existing) {
    await supabase.from('push_tokens')
      .update({ user_id: userId, platform: 'ios', updated_at: new Date().toISOString() })
      .eq('id', existing.id)
  } else {
    await supabase.from('push_tokens').insert({
      user_id: userId,
      token,
      platform: 'ios',
      environment: ENVIRONMENT,
    })
  }
}

export async function registerPushNotifications(userId: string) {
  if (!Capacitor.isNativePlatform()) return

  const { receive } = await PushNotifications.checkPermissions()
  let status = receive

  if (status === 'prompt') {
    const result = await PushNotifications.requestPermissions()
    status = result.receive
  }

  if (status !== 'granted') return

  await registrationListener?.remove()
  await registrationErrorListener?.remove()

  registrationListener = await PushNotifications.addListener('registration', ({ value: token }) => {
    saveToken(userId, token)
  })

  registrationErrorListener = await PushNotifications.addListener('registrationError', (err) => {
    console.error('Push registration error:', err)
  })

  await PushNotifications.register()
}
